import React, { useMemo, useState, useCallback, useRef, useEffect, forwardRef } from 'react';
import { Popover, Input, Icon } from '@Sage/hoi-poi-ui';
import classnames from 'classnames';
import AdvancedSelectMenu from './AdvancedSelectMenu';

import './styles.scss';

const AdvancedSelect = forwardRef(
    (
        {
            key,
            menuTitle,
            filterOptions,
            defaultFilter,
            options,
            searchPlaceholder,
            filterPlaceholder,
            children,
            onChange,
            onChangeVisible,
            value,
            ...props
        },
        ref,
    ) => {
        const [visible, setVisible] = useState(false);
        const contentRef = useRef(null);

        const onClickOutside = useCallback(
            (e) => {
                if (!contentRef.current?.contains(e.target)) {
                    onChangeVisible?.(false);
                    setVisible(false);
                }
            },
            [onChangeVisible],
        );

        const onClick = useCallback(
            (e) => {
                if (e.target.closest('.fm-advanced-select__input-post') === null) {
                    onChangeVisible?.(!visible);
                    setVisible(!visible);
                }
            },
            [onChangeVisible, visible],
        );

        const onChangeField = useCallback(
            (option) => {
                onChange(option);
                onChangeVisible?.(false);
                setVisible(false);
            },
            [onChange, onChangeVisible],
        );

        const onChangeInput = useCallback(
            (e) => {
                onChange();
                onChangeVisible?.(false);
                setVisible(false);
            },
            [onChange, onChangeVisible],
        );

        const inputRef = useCallback((ref) => {
            if (ref) ref.readOnly = true;
        }, []);

        useEffect(() => {
            document.addEventListener('mousedown', onClickOutside);
            return () => {
                document.removeEventListener('mousedown', onClickOutside);
            };
        }, [onClickOutside]);

        const popoverProps = useMemo(
            () => ({
                className: 'fm-advanced-select__popover',
                placement: 'bottomLeft',
                content: visible ? (
                    <div
                        ref={(cRef) => {
                            if (ref) ref.current = cRef;
                            contentRef.current = cRef;
                        }}
                    >
                        <AdvancedSelectMenu
                            menuTitle={menuTitle}
                            filterOptions={filterOptions}
                            defaultFilter={defaultFilter}
                            options={options}
                            searchPlaceholder={searchPlaceholder}
                            filterPlaceholder={filterPlaceholder}
                            onChange={onChangeField}
                            value={value}
                            visible={visible}
                        />
                    </div>
                ) : null,
                destroyTooltipOnHide: true,
                visible,
            }),
            [
                defaultFilter,
                filterOptions,
                menuTitle,
                onChangeField,
                options,
                ref,
                filterPlaceholder,
                searchPlaceholder,
                value,
                visible,
            ],
        );

        const inputProps = useMemo(
            () => ({
                ...props,
                className: classnames(props.className, 'fm-advanced-select__input', {
                    'fm-advanced-select__input--focused': visible,
                }),
                classes: {
                    clear: 'fm-advanced-select__input-post',
                },
                postComponent: (
                    <Icon name="arrowDropDown" className="fm-advanced-select__input__dropdown" />
                ),
                value: value?.label,
                onChange: onChangeInput,
                ref: inputRef,
            }),
            [props, visible, value?.label, onChangeInput, inputRef],
        );

        return (
            <Popover {...popoverProps}>
                <div className="fm-advanced-select" onClick={onClick}>
                    {!children && <Input {...inputProps} />}
                    {children}
                </div>
            </Popover>
        );
    },
);

export default AdvancedSelect;
